import { BPLAYER_URLS, SPRITZ_URL } from './constants';
import {
  BPlayer,
  PlayerEnvironments,
  Spritz,
  VideoPlayerImportOptions,
} from './types';

export function loadScript(
  src: string,
  type?: string
): Promise<Spritz | undefined> {
  // after a minute we should fail
  const MAX_TIMEOUT_FAIL = 60 * 1000;

  // Using old-school script append instead of dynamic
  // import to prevent issues with Webpack bundlers
  // In order for webpack to work, we need a /* webpackIgnore: true */
  // comment. However, if this comment gets stripped, we will get an error
  // Too fiddly for my tastes
  // https://github.com/webpack/webpack/issues/8341
  return new Promise((resolve, reject) => {
    // if spritz already exists on the page,
    // early return
    if (window.Spritz) {
      resolve(window.Spritz);
    }

    // if the script is on the page already
    const existingScript = document.getElementById('mui-spritz');

    // if we haven't loaded spritz before
    if (!existingScript || existingScript.tagName !== 'SCRIPT') {
      const script = document.createElement('script');

      script.src = src;
      script.async = true;
      script.setAttribute('id', 'mui-spritz');
      script.setAttribute('type', type || 'text/javascript');

      // load it and listen for load to resolve
      script.onload = () => {
        resolve(window.Spritz);
      };

      // handle errors
      script.onerror = (err) => reject(err);

      document.body.appendChild(script);
    }

    // hold a reference to a setTimeout
    let timeout = 0;

    const pollForSpritzLoad = (duration: number) => {
      if (window.Spritz) {
        clearTimeout(timeout);
        resolve(window.Spritz);
      }

      const nextInterval = duration * 2;

      if (nextInterval > MAX_TIMEOUT_FAIL) {
        const err = new Error('Media-UI: Spritz unable to load on the window');

        reject(err);
      }

      // backoff
      timeout = setTimeout(
        pollForSpritzLoad,
        // backoff from 300ms to approx. 16 mins
        nextInterval
      );
    };

    pollForSpritzLoad(300);
  });
}

export async function importBPlayer(
  env: PlayerEnvironments,
  options?: VideoPlayerImportOptions
): Promise<BPlayer> {
  if (typeof window === 'undefined') {
    const err = 'Video player can only be imported on the client';

    throw new Error(err);
  }

  if (window.BPlayer) {
    return window.BPlayer;
  }

  try {
    const spritz = await loadScript(SPRITZ_URL, options?.scriptType);

    await spritz?.inject(BPLAYER_URLS[env] as string);

    return window.BPlayer;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Media-UI: Unable to load BPlayer via spritz ${err}`);
    throw err;
  }
}
